import { getErrorCopy } from '@/shared/copy/errors';
import { getMessage } from '@/shared/copy/messages';
import { isStaleClerkOrganisationLinkError } from '@/shared/data/client';
import {
  clearOwnedAuthBootstrapEvent,
  markAuthBootstrapFinished,
  markAuthBootstrapStarted,
  waitForCoordinatedAuthBootstrap,
} from './bootstrapCoordination';
import {
  clearSessionExpiredFlag,
  getManualSignOutFlag,
  setClerkProvisioningFlag,
  setManualSignOutFlag,
} from './sessionStorage';
import {
  bootstrapRetryIntervalMs,
  bootstrapTimeoutMs,
  isAccountNotFoundError,
  isIdentityMismatchError,
  mergeSessionFallbacks,
  shouldPreserveSessionOnSignedOutSignal,
  wait,
} from './useAuth.helpers';
import type { AuthError, AuthSession } from './types';

type ExecuteAuthBootstrapArgs = {
  applySession: (next: AuthSession | null) => void;
  authBootstrapOwnerId: string;
  clearAuthRecoveryFailures: () => void;
  recoverStaleClerkOrganisation: () => Promise<boolean>;
  clerkSignOut: () => Promise<void>;
  currentSession: AuthSession | null;
  hasSignedInSignal: boolean;
  loadCurrentClerkSession: () => Promise<AuthSession | null>;
  setError: (error: AuthError | null) => void;
};

type BootstrapOutcome = {
  loading: boolean;
};

export const executeAuthBootstrap = async ({
  applySession,
  authBootstrapOwnerId,
  clearAuthRecoveryFailures,
  recoverStaleClerkOrganisation,
  clerkSignOut,
  currentSession,
  hasSignedInSignal,
  loadCurrentClerkSession,
  setError,
}: ExecuteAuthBootstrapArgs): Promise<BootstrapOutcome> => {
  let bootstrapEventStartedAt: number | null = null;

  if (getManualSignOutFlag()) {
    applySession(null);
    return { loading: false };
  }

  try {
    if (!hasSignedInSignal) {
      if (
        shouldPreserveSessionOnSignedOutSignal({
          hasSignedInSignal,
          currentSession,
        })
      ) {
        return { loading: false };
      }
      setManualSignOutFlag(false);
      setClerkProvisioningFlag(false);
      clearSessionExpiredFlag();
      applySession(null);
      return { loading: false };
    }

    const coordinatedBootstrapResult = await waitForCoordinatedAuthBootstrap(authBootstrapOwnerId);
    if (coordinatedBootstrapResult === 'succeeded') {
      await wait(50);
    }

    const startedAt = Date.now();
    bootstrapEventStartedAt = markAuthBootstrapStarted(authBootstrapOwnerId);
    let refreshed: AuthSession | null = null;
    while (Date.now() - startedAt < bootstrapTimeoutMs) {
      refreshed = await loadCurrentClerkSession();
      if (refreshed) {
        break;
      }
      await wait(bootstrapRetryIntervalMs);
    }

    if (!refreshed) {
      clearAuthRecoveryFailures();
      setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.loadSessionFailed') });
      setClerkProvisioningFlag(false);
      clearSessionExpiredFlag();
      applySession(null);
      markAuthBootstrapFinished(authBootstrapOwnerId, 'failed');
      return { loading: false };
    }

    clearAuthRecoveryFailures();
    setError(null);
    clearSessionExpiredFlag();
    applySession(mergeSessionFallbacks(refreshed, currentSession));
    markAuthBootstrapFinished(authBootstrapOwnerId, 'succeeded');
    return { loading: false };
  } catch (error) {
    if (isStaleClerkOrganisationLinkError(error)) {
      const recovered = await recoverStaleClerkOrganisation();
      if (recovered) {
        markAuthBootstrapFinished(authBootstrapOwnerId, 'failed');
        return { loading: false };
      }
    }

    if (isIdentityMismatchError(error)) {
      setError({
        code: 'AUTH_ERROR',
        message: 'Account identity changed. Please sign in again.',
      });
      setClerkProvisioningFlag(false);
      setManualSignOutFlag(true);
      applySession(null);
      markAuthBootstrapFinished(authBootstrapOwnerId, 'failed');
      void clerkSignOut();
      return { loading: false };
    }

    if (isAccountNotFoundError(error)) {
      setError({
        code: 'AUTH_ERROR',
        message: getMessage('auth.signIn.accountNotFound'),
      });
      setClerkProvisioningFlag(false);
      setManualSignOutFlag(true);
      applySession(null);
      markAuthBootstrapFinished(authBootstrapOwnerId, 'failed');
      return { loading: false };
    }

    setError({ code: 'AUTH_ERROR', message: getErrorCopy('auth.loadSessionFailed') });
    setClerkProvisioningFlag(false);
    clearSessionExpiredFlag();
    applySession(null);
    markAuthBootstrapFinished(authBootstrapOwnerId, 'failed');
    return { loading: false };
  } finally {
    if (bootstrapEventStartedAt !== null) {
      clearOwnedAuthBootstrapEvent(authBootstrapOwnerId, bootstrapEventStartedAt);
    }
  }
};
